import React, { useState, useRef, useMemo, useEffect } from "react";
import JsPDF from "jspdf";
import html2canvas from "html2canvas";
import styled from "styled-components";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import Chevron from "../../../../../assets/images/icons/Web_Sprite_Icon_Chevron_Grey_analytics.png";
import TZLogo from "../../../../../assets/images/logos/colorLogo.png";
import HeroLogoIntraverse from "../../../../../assets/images/logos/HeroLogoIntraverse.png";
import { capitalizeFirstLetter, createCrownPlugin } from "../../../../../utils/generalUtils";
import { ReactComponent as ExportIcon } from "../../../../../assets/images/icons/Mediamodifier-Design.svg";

ChartJS.register(ArcElement, Tooltip, Legend);

const BarColorPalette = ["#6E39E2", "#9D75F5", "#3849E0", "#576AFC", "#57BFFC", "#57FCFC"];

const Piechart = ({
    chartTitle,
    data,
    isFilter,
    totalVisit,
    displayDate,
    isPieChartModalOpen,
    setIsPieChartModalOpen,
    disablePopUp,
    headerTextKey,
    headerTextValue,
    pdfStartDate,
    pdfEndDate,
    client,
}) => {
    const chartRef = useRef(null);
    const dropdownRef = useRef(null);
    const [isCategoryOpen, setIsCategoryOpen] = useState(false);
    const [selectedCategories, setSelectedCategories] = useState(["All"]);
    const categories = ["All", ...Array.from(new Set(data?.map((item) => item.name)))];

    const filteredData = useMemo(() => {
        if (selectedCategories.includes("All")) return data;
        return data.filter((item) => selectedCategories.includes(item.name));
    }, [data, selectedCategories]);

    const safeData = Array.isArray(filteredData) ? filteredData : [];

    const topData = safeData.slice(0, 5).map((item) => ({
        ...item,
        visits: Number(item.visits),
    }));

    const totalVisitNum = safeData.reduce((sum, item) => sum + Number(item.visits), 0);
    const othersData = safeData.slice(5).map((item) => ({
        ...item,
        visits: Number(item.visits),
    }));
    const othersVisits = othersData.reduce((sum, item) => sum + item.visits, 0);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsCategoryOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);
    const displayName = (name) => (name.length > 30 ? `${name.slice(0, 30)}...` : name);
    const displayNamePDF = (name) => (name.length > 60 ? `${name.slice(0, 60)}...` : name);

    const chartData = {
        labels: [...topData?.map((item) => displayName(item.name)), othersVisits > 0 ? "Others" : ""].filter(
            (label) => label
        ),
        datasets: [
            {
                data: [...topData?.map((item) => item.visits), othersVisits > 0 ? othersVisits : null].filter(
                    (value) => value !== null
                ),
                backgroundColor: BarColorPalette.slice(0, topData.length + (othersVisits > 0 ? 1 : 0)),
                borderWidth: 1,
                borderColor: "transparent",
                cutout: "60%",
                spacing: 10,
                borderRadius: 1,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                titleFont: {
                    family: "Barlow",
                    size: 12,
                    style: "normal",
                    weight: "700",
                },
                titleColor: "rgba(255, 255, 255, 0.50)",
                bodyFont: {
                    family: "Barlow",
                    size: 12,
                    weight: "400",
                },
                displayColors: false,
                callbacks: {
                    title: () => displayDate,
                    label: (context) => {
                        const { label } = context;
                        const value = context.parsed;
                        const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
                        const percent = total > 0 ? ((value / total) * 100).toFixed(1) : 0;

                        if (label === "Others") {
                            const othersItems = filteredData.slice(5); // ✅ use filtered data
                            const othersLines = othersItems.map((item) => {
                                const itemPercent = total > 0 ? ((item.visits / total) * 100).toFixed(1) : 0;
                                return `${item.name}: ${itemPercent}%`;
                            });
                            return [`Others`, ...othersLines];
                        }

                        return `${label}: ${percent}%`;
                    },
                },
            },
        },
    };

    const legendData = [
        ...topData?.map((item, index) => ({
            namePdf: displayNamePDF(item.name),
            name: displayName(item.name),
            visits: item.visits,
            color: BarColorPalette[index],
        })),
        ...(othersVisits > 0
            ? [
                  {
                      name: "Others",
                      namePdf: "Others",
                      visits: othersVisits,
                      color: BarColorPalette[topData.length],
                      children: othersData.map((child) => ({
                          namePdf: displayNamePDF(child.name),
                          name: displayName(child.name),
                          visits: child.visits,
                          percent: totalVisitNum > 0 ? `${((child.visits / totalVisitNum) * 100).toFixed(1)}%` : "0%",
                      })),
                  },
              ]
            : []),
    ];

    const handleExportPDF = async () => {
        if (!data || data.length === 0) return;

        const pdf = new JsPDF("landscape", "pt", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 40;
        const chartSize = 200; // keep pie chart square
        const paddingBetweenChartAndLegends = 30;

        // ✅ Get chart canvas
        const chartInstance = chartRef.current;
        if (!chartInstance) return;

        const chartCanvas = chartInstance.canvas;
        const chartImage = chartCanvas.toDataURL("image/png", 1.0);

        // Black background
        pdf.setFillColor(0, 0, 0);
        pdf.rect(0, 0, pageWidth, pageHeight, "F");

        // ✅ Add watermark
        pdf.saveGraphicsState();
        pdf.setGState(new pdf.GState({ opacity: 0.2 }));
        const wmWidth = 450;
        const wmHeight = 400;
        const wmX = (pageWidth - wmWidth) / 2;
        const wmY = pageHeight * 0.15;
        pdf.addImage(HeroLogoIntraverse, "PNG", wmX, wmY, wmWidth, wmHeight, "", "NONE", 0);
        pdf.restoreGraphicsState();

        // ✅ Header
        const headerY = 30;
        const logoWidth = 70;
        const logoHeight = 70;
        pdf.addImage(TZLogo, "PNG", margin, headerY, logoWidth, logoHeight);
        const spacingBetweenLogos = 15;
        const textStartX = margin + logoWidth + spacingBetweenLogos + 10;

        pdf.setFontSize(14);
        pdf.setTextColor(255, 255, 255);
        pdf.text(`${capitalizeFirstLetter(client)}: ${chartTitle}`, textStartX, headerY + 20, { align: "left" });

        pdf.setFontSize(10);
        pdf.text(`Duration: ${displayDate}`, textStartX, headerY + 40, { align: "left" });
        pdf.text(`(${pdfStartDate} - ${pdfEndDate})`, textStartX, headerY + 55, { align: "left" });

        // ✅ Add Pie chart centered
        const chartX = margin;
        const chartY = headerY + logoHeight + 90;
        pdf.addImage(chartImage, "PNG", chartX, chartY, chartSize, chartSize);

        // ✅ Layout for PDF legend table
        const legendStartX = chartX + chartSize + 40;
        const legendStartY = headerY + logoHeight + 90;
        const lineHeight = 18;
        const totalVisits = legendData.reduce((sum, item) => sum + (item.visits || 0), 0);

        const enrichedLegendData = legendData.map((item, i) => ({
            ...item,
            index: i + 1,
            percent: totalVisits > 0 ? `${((item.visits / totalVisits) * 100).toFixed(1)}%` : "0%",
        }));

        let currentY = legendStartY;
        const footerHeight = 40;
        const maxY = pageHeight - footerHeight;
        // ✅ Header row for legends
        pdf.setFontSize(10);
        pdf.setTextColor(255, 255, 255);
        pdf.text("Name", legendStartX, currentY, { style: "bold" });

        pdf.text("Visits", legendStartX + 300, currentY, { align: "center", style: "bold" });

        pdf.text("%", legendStartX + 450, currentY, { align: "right", style: "bold" });
        currentY += lineHeight;
        enrichedLegendData.forEach((item) => {
            // If there's no more space, create new page
            if (currentY + lineHeight > maxY) {
                pdf.addPage();
                // redraw black background
                pdf.setFillColor(0, 0, 0);
                pdf.rect(0, 0, pageWidth, pageHeight, "F");

                // redraw header on each page if needed
                pdf.setFontSize(14);
                pdf.setTextColor(255, 255, 255);
                pdf.text(`${capitalizeFirstLetter(client)}: ${chartTitle}`, margin, 40);

                // reset Y
                currentY = margin + 40;
                // re-draw legend header row
                pdf.setFontSize(10);
                pdf.setTextColor(255, 255, 255);
                pdf.text("Name", legendStartX, currentY, { style: "bold" });
                pdf.text("Visits", legendStartX + 300, currentY, { align: "center", style: "bold" });
                pdf.text("%", legendStartX + 450, currentY, { align: "right", style: "bold" });
                currentY += lineHeight;
            }

            // Draw the legend row
            const swatchSize = 8;
            pdf.setFillColor(item.color || "#000000");
            pdf.rect(legendStartX - 15, currentY - swatchSize, swatchSize, swatchSize, "F");

            pdf.setTextColor(255, 255, 255);
            pdf.text(`${item.index}. ${item.namePdf}`, legendStartX, currentY);

            pdf.setTextColor(200, 200, 200);
            pdf.text(String(item.visits), legendStartX + 300, currentY, { align: "center" });

            pdf.setTextColor(180, 180, 180);
            pdf.text(item.percent, legendStartX + 450, currentY, { align: "right" });

            currentY += lineHeight;

            if (item.namePdf === "Others" && Array.isArray(item.children)) {
                pdf.setDrawColor(100, 100, 100);
                pdf.setLineWidth(0.5);
                pdf.line(legendStartX, currentY - lineHeight / 2, legendStartX + 450, currentY - lineHeight / 2);
                currentY += 5;
                item.children.forEach((child) => {
                    if (currentY + lineHeight > maxY) {
                        pdf.addPage();
                        pdf.setFillColor(0, 0, 0);
                        pdf.rect(0, 0, pageWidth, pageHeight, "F");

                        currentY = margin + 40;
                    }

                    pdf.setTextColor(200, 200, 200);
                    pdf.text(`- ${child.namePdf}`, legendStartX + 20, currentY);

                    pdf.setTextColor(160, 160, 160);
                    pdf.text(String(child.visits), legendStartX + 300, currentY, { align: "center" });

                    pdf.setTextColor(140, 140, 140);
                    pdf.text(child.percent, legendStartX + 450, currentY, { align: "right" });

                    currentY += lineHeight;
                });
            }
        });
        const pageCount = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i += 1) {
            pdf.setPage(i);
            pdf.setFontSize(9);
            pdf.setTextColor(180, 180, 180);
            pdf.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 20, { align: "center" });
        }

        const filename = `${client.toLowerCase()}_${chartTitle.replace(/\s+/g, "_").toLowerCase()}_${Date.now()}.pdf`;
        pdf.save(filename);
    };

    return (
        <PiechartContainer>
            <PiechartHeader>
                {!isPieChartModalOpen && (
                    <>
                        <PiechartTitle>{chartTitle}</PiechartTitle>
                        <ChartFilter isFilter={isFilter}>
                            <FilterText>
                                {(() => {
                                    if (selectedCategories.includes("All")) return "All";
                                    if (selectedCategories.length > 2) return `${selectedCategories.length} selected`;
                                    return selectedCategories.join(", ");
                                })()}
                            </FilterText>

                            <ImageContainer
                                src={Chevron}
                                width={"16px"}
                                height={"16px"}
                                padding={"2.235px 0px"}
                                alt="chevron"
                                isChevron
                                isFlagOpen={isCategoryOpen}
                                onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                            />
                            {isCategoryOpen && (
                                <DropdownContainer ref={dropdownRef}>
                                    {categories.map((cat, idx) => (
                                        <DropdownItem
                                            key={idx}
                                            onClick={() => {
                                                if (cat === "All") {
                                                    setSelectedCategories(["All"]);
                                                } else {
                                                    let updated = [...selectedCategories];
                                                    if (updated.includes(cat)) {
                                                        updated = updated.filter((c) => c !== cat);
                                                    } else {
                                                        updated.push(cat);
                                                    }
                                                    updated = updated.filter((c) => c !== "All");
                                                    setSelectedCategories(updated.length ? updated : ["All"]);
                                                }
                                            }}
                                            style={{
                                                fontWeight: selectedCategories.includes(cat) ? "700" : "400",
                                            }}
                                        >
                                            {cat}
                                        </DropdownItem>
                                    ))}
                                </DropdownContainer>
                            )}
                        </ChartFilter>
                        {totalVisit > 0 && (
                            <IconWrapper onClick={handleExportPDF}>
                                <StyledExportIcon />
                                <TooltipIcon>Export PDF</TooltipIcon>
                            </IconWrapper>
                        )}
                    </>
                )}

                {isPieChartModalOpen && (
                    <>
                        <PopupDateTitle>{displayDate}</PopupDateTitle>
                        <TotalVisits>
                            Total Visits: <span>{totalVisit.toLocaleString()}</span>
                        </TotalVisits>
                    </>
                )}
            </PiechartHeader>

            <ChartWrapper isPieChartModalOpen={isPieChartModalOpen}>
                <PieChartContainer>
                    <Pie ref={chartRef} data={chartData} options={options} />
                </PieChartContainer>
                {!isPieChartModalOpen ? (
                    <LegendContainer>
                        {legendData.map((item, index) => (
                            <LegendItem key={index}>
                                <ColorSquare color={item.color} />
                                <LegendContentContainer>
                                    <LegendName>{item.name}</LegendName>
                                    <LegendPercentage>
                                        {((item.visits / totalVisitNum) * 100).toFixed(1)}%
                                    </LegendPercentage>
                                </LegendContentContainer>
                            </LegendItem>
                        ))}
                    </LegendContainer>
                ) : (
                    <ModalLegendContainer>
                        <ModalLegendHeader
                            style={{ display: "grid", gridTemplateColumns: "60% 20% 20%", alignItems: "center" }}
                        >
                            <HeaderCell>{headerTextKey}</HeaderCell>
                            <HeaderCell style={{ textAlign: "center" }}>Visits</HeaderCell>
                            <HeaderCell style={{ textAlign: "right" }}>{headerTextValue}</HeaderCell>
                        </ModalLegendHeader>

                        {data.map((item, index) => (
                            <ListItem
                                key={item.name}
                                isOdd={index % 2 === 0}
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "60% 20% 20%",
                                    alignItems: "center",
                                    gap: "8px",
                                }}
                            >
                                {/* Combine ColorSquare + Name into first column */}
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <ColorSquare color={index < 6 ? BarColorPalette[index] : "#57FCFC"} />
                                    <Name>{`${index + 1}. ${item.name}`}</Name>
                                </div>

                                <VisitNumber style={{ textAlign: "center" }}>{item.visits}</VisitNumber>
                                <Percentage style={{ textAlign: "right" }}>
                                    {((item.visits / totalVisitNum) * 100).toFixed(1)}%
                                </Percentage>
                            </ListItem>
                        ))}
                    </ModalLegendContainer>
                )}
            </ChartWrapper>

            {!isPieChartModalOpen && (
                <ChartFooter>
                    <TotalVisits>
                        Total Visits: <span>{totalVisit.toLocaleString()}</span>
                    </TotalVisits>
                    <ChartDetailsContainer disablePopUp={disablePopUp} onClick={() => setIsPieChartModalOpen(true)}>
                        <ChartDetailText>View more details</ChartDetailText>
                        <ImageContainer
                            src={Chevron}
                            width={"17px"}
                            height={"17px"}
                            padding={"1.324px 0.294px"}
                            alt="chevron"
                            style={{ rotate: "180deg" }}
                        />
                    </ChartDetailsContainer>
                </ChartFooter>
            )}
        </PiechartContainer>
    );
};

export default Piechart;

const PiechartContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
`;

const PiechartHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;

    @media (max-width: 768px) {
        width: 100%;
        flex-direction: column;
    }
`;

const PiechartTitle = styled.div`
    color: var(--Text-Primary, #fbfcff);
    font-family: "Barlow";
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    text-transform: capitalize;
    flex: 1;
`;

const ChartFilter = styled.div`
    position: relative;
    display: ${(props) => (props.isFilter ? "flex" : "none")};
    padding: 10px 16px;
    align-items: center;
    gap: 16px;
    border-radius: 77px;
    border: 1px solid var(--purple-secondary-30, rgba(157, 117, 245, 0.3));

    @media (max-width: 768px) {
        width: 80%;
    }
`;

const FilterText = styled.p`
    color: var(--White---Black-White, #fbfcff);
    font-family: "Barlow";
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    letter-spacing: 0.5px;

    @media (max-width: 768px) {
        flex: 1;
    }
`;

const FilterChevron = styled.img`
    display: flex;
    width: 14px;
    height: 14px;
    padding: 0.926px 0.206px;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    rotate: -90deg;
`;

const ChartWrapper = styled.div`
    margin-top: 30px;
    display: flex;
    align-items: center;
    gap: 24px;
    flex-direction: ${(props) => (props.isPieChartModalOpen ? "column" : "row")};

    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const PieChartContainer = styled.div`
    width: 200px;
    height: 200px;
`;

const LegendContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
`;

const LegendContentContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`;

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ColorSquare = styled.div`
    width: 16px;
    height: 16px;
    background-color: ${({ color }) => color};
    border-radius: 10px;
`;

const LegendName = styled.span`
    color: var(--Text-Primary, #fbfcff);
    font-family: "Barlow";
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    flex: 1;
`;

const LegendPercentage = styled.span`
    color: var(--Text-Secondary, #ced1d9);
    font-family: "Barlow";
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
`;

const TotalVisits = styled.div`
    color: var(--Text-Secondary, #ced1d9);
    text-align: right;
    font-family: "Barlow";
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;

    span {
        color: var(--Text-Primary, #fbfcff);
        font-family: "Barlow";
        font-size: 16px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
    }
`;

const ChartFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    margin-top: 25px;
`;

const ChartDetailsContainer = styled.div`
    display: ${(props) => (props.disablePopUp ? "none" : "flex")};
    align-items: center;
    gap: 6px;
    cursor: pointer;
`;

const ChartDetailText = styled.div`
    color: var(--Purple-Secondary, #9d75f5);
    text-align: right;
    font-family: "Barlow";
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;

    @media (max-width: 768px) {
        font-size: 16px;
    }
`;

const ModalLegendContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: 16px;
    font-family: "Barlow";
`;

const ModalLegendHeader = styled.div`
    display: grid;
    grid-template-columns: 48px minmax(146px, 249px) 324px 153px;
    align-items: center;
    padding: 8px 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    font-weight: 600;
    font-size: 16px;
`;

const HeaderCell = styled.div`
    color: var(--Text-Secondary, #ced1d9);
    font-size: 16px;
`;

const ListItem = styled.div`
    display: grid;
    grid-template-columns: 32px minmax(120px, 250px) 60px 60px; /* same as header */
    align-items: center;
    padding: 8px 16px;
    background-color: ${({ isOdd }) => (isOdd ? "rgba(255, 255, 255, 0.04)" : "transparent")};
    column-gap: 8px; /* smaller gap */
`;

const HeaderText = styled.div`
    color: var(--Text-Secondary, #ced1d9);
    font-family: "Barlow";
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
`;

const Name = styled.div`
    color: var(--Text-Primary, #fbfcff);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const VisitNumber = styled.div`
    color: var(--Text-Primary, #fbfcff);
    text-align: right;
    font-weight: 400;
`;

const Percentage = styled.div`
    color: var(--Text-Primary, #fbfcff);
    text-align: right;
    font-weight: 600;
`;

const PopupDateTitle = styled.p`
    color: var(--White---Black-Light-Grey, #9d9d9d);
    leading-trim: both;
    text-edge: cap;
    font-family: "Barlow";
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: 1.8px;
    text-transform: uppercase;
`;

const ImageContainer = styled.img`
    display: flex;
    width: ${(props) => (props.width ? props.width : "14px")};
    height: ${(props) => (props.height ? props.height : "14px")};
    padding: ${(props) => (props.padding ? props.padding : "1px 1px")};
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transform: ${(props) => {
        if (props.isChevron && props.isFlagOpen) return "rotate(90deg)";
        if (props.isChevron) return "rotate(-90deg)";
        return "none";
    }};
    transition: transform 0.2s;
`;

const DropdownContainer = styled.div`
    position: absolute;
    top: calc(100% + 4px); /* exactly below the filter with 4px gap */
    left: 0; /* align to left edge of ChartFilter */
    background-color: #1e1e2f;
    border: 1px solid rgba(157, 117, 245, 0.3);
    border-radius: 8px;
    padding: 8px 0;
    z-index: 10;
    width: max-content;
    max-height: 250px;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: rgba(157, 117, 245, 0.5) transparent;

    &::-webkit-scrollbar {
        width: 6px;
    }
    &::-webkit-scrollbar-thumb {
        background: rgba(157, 117, 245, 0.5);
        border-radius: 3px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
`;

const DropdownItem = styled.div`
    padding: 8px 16px;
    font-family: "Barlow";
    font-size: 14px;
    color: #fbfcff;
    cursor: pointer;
    &:hover {
        background-color: rgba(157, 117, 245, 0.2);
    }
`;

const IconWrapper = styled.div`
    position: relative;
    display: inline-block;
    cursor: pointer;
    margin-left: auto;

    &:hover span {
        visibility: visible;
        opacity: 1;
    }
`;

const StyledExportIcon = styled(ExportIcon)`
    width: 40px;
    height: 40px;
`;

const TooltipIcon = styled.span`
    visibility: hidden;
    opacity: 0;
    position: absolute;
    bottom: -30px; /* below the icon */
    left: 50%;
    transform: translateX(-50%);
    background-color: #333;
    color: #fff;
    font-size: 12px;
    font-family: "Barlow", sans-serif;
    padding: 4px 8px;
    border-radius: 4px;
    white-space: nowrap;
    transition: opacity 0.2s ease;
`;
